<template>
  <div v-if="customFieldListItems.length > 0" class="custom-field-filters">
    <WdsStack justify-space-between align-center>
      <WdsText secondary>
        {{ $t('Custom fields') }}
      </WdsText>
      <WdsButtonGhost :disable="customFields.length === 0" secondary @click="addCustomFieldFilter">
        <WdsStack gap-xs align-center>
          <WdsIcon name="plus" :size="16" />
          {{ $t('Create filter') }}
        </WdsStack>
      </WdsButtonGhost>
    </WdsStack>
    <WdsDivider class="tw-mt-0.5" />
    <WdsStack
      v-for="fieldFilter in customFieldFilters"
      :key="fieldFilter.key"
      class="tw-w-full"
      direction-column
      align-start
      gap-sm
    >
      <WdsText
        class="tw-w-full"
        :sub-text="fieldFilter.field ? customFieldLookup[fieldFilter.field].description : ''"
      >
        <WdsStack justify-space-between align-center class="tw-mt-2.5">
          <span>
            {{
              fieldFilter.field
                ? customFieldLookup[fieldFilter.field].title
                : $t('Select a field to filter by')
            }}
          </span>
          <WdsButtonIcon transparent-background sm @click="removeCustomFieldFilter(fieldFilter)">
            <WdsIcon name="trash-can" :size="16" />
          </WdsButtonIcon>
        </WdsStack>
      </WdsText>
      <WdsSelect
        v-if="fieldFilter.field === null"
        class="tw-w-full"
        :list="customFieldListItems"
        :placeholder="$t('Custom fields')"
        @update:model-value="fieldSelected(fieldFilter, $event)"
      />
      <WdsSelect
        v-else-if="customFieldLookup[fieldFilter.field].fieldType === CustomFieldType.SingleSelect"
        v-model="fieldFilter.value"
        class="tw-w-full"
        :list="customFieldLookup[fieldFilter.field].customFieldOptions"
        :placeholder="$t('Select option to filter by')"
      />
      <WdsInput
        v-else
        v-model="fieldFilter.value as string | null"
        :placeholder="$t('Text to search for')"
      />
    </WdsStack>
  </div>
</template>

<script setup lang="ts">
import {
  WdsButtonGhost,
  WdsButtonIcon,
  WdsDivider,
  WdsIcon,
  WdsInput,
  WdsSelect,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { computed, reactive, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import uniqBy from 'lodash/uniqBy'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import {
  CustomField,
  CustomFieldOption,
  CustomFieldType,
  CustomFieldTypeValueInput,
  TypeSupportingCustomFieldValues,
} from '@/gql/graphql'
import { uuid } from '@/utils/tools'
import { CompanyQuery } from '@/components/Contacts/CompanyFreelancer/queries/Company'
import { CustomFieldFilter } from '@/components/Fields/custom-fields-types'

const customFieldFilters = defineModel<CustomFieldFilter[]>('customFieldFilters', {
  // The reactive bit has to be done due to a bug in vue 3 for arrays nad defineModel, when no parent binding is
  // provided: https://github.com/vuejs/core/issues/11143
  default: reactive([]),
})

const emit = defineEmits<{ gqlFilters: [filter: CustomFieldTypeValueInput[]] }>()

const { result, loading } = useQuery(
  CompanyQuery,
  { companyId: currentAccount.globalId },
  {
    clientId: 'publicApiCustomFields',
  },
)

const getFields = (company) => company.customFields?.data
const appliesToTC = (customField) =>
  customField.appliesTo === TypeSupportingCustomFieldValues.TrustedContact

const customFields = computed(() => {
  if (loading.value) return []
  const companyFields = getFields(result.value?.company).flat()

  // List of accounts
  return uniqBy<any>(companyFields.flat().filter(appliesToTC), (field) => field.id)
})

const customFieldLookup = computed(() =>
  // Makes an object of type id: customField to use for lookup
  customFields.value.reduce((acc, customField) => ({ ...acc, [customField.id]: customField }), {}),
)

const customFieldListItems = computed(() =>
  customFields.value.map((customField) => ({ label: customField.title, value: customField })),
)

const addCustomFieldFilter = () => {
  customFieldFilters.value.push({
    field: null,
    value: null,
    type: null,
    key: uuid(),
  })
}

const fieldSelected = (field: CustomFieldFilter, $event: { label: string; value: CustomField }) => {
  // eslint-disable-next-line no-param-reassign
  field.field = $event.value.id
  // eslint-disable-next-line no-param-reassign
  field.type = $event.value.fieldType
}

const removeCustomFieldFilter = (customField: CustomFieldFilter) => {
  const index = customFieldFilters.value.findIndex(({ key }) => key === customField.key)
  if (index !== -1) {
    customFieldFilters.value.splice(index, 1)
  }
}

// We watch the reactive array rather than the shallow ref that it comes packaged in
watch(customFieldFilters.value, (filters: CustomFieldFilter[]) => {
  const customFieldsGqlInput: CustomFieldTypeValueInput[] = filters
    .filter(
      (customField: CustomFieldFilter) => customField.field !== null && customField.value !== null,
    )
    .map((customField: CustomFieldFilter) => {
      switch (customField.type) {
        case CustomFieldType.SingleSelect:
          return {
            freeText: null,
            singleSelect: {
              id: customField.field ?? '',
              slug: null, // Graphql requirement
              value: (customField.value as CustomFieldOption)?.id ?? '',
            },
          }
        case CustomFieldType.FreeText:
          return {
            freeText: {
              id: customField.field ?? '',
              slug: null, // Graphql requirement
              value: (customField.value as string) ?? '',
            },
            singleSelect: null,
          }
        default:
          return {
            freeText: null,
            singleSelect: null,
          }
      }
    })

  emit('gqlFilters', customFieldsGqlInput)
})
</script>

<style scoped lang="scss">
.custom-field-filters {
  :deep(.wds-input-wrapper) {
    width: 100%;
  }
}
</style>
